import React, {useState} from 'react';
import styled from 'styled-components';
import {Button, EMPHASIS} from '@forecast-it/design-system';

type IconName = React.ComponentProps<typeof Button>['icon'];
type Emphasis = React.ComponentProps<typeof Button>['emphasis'];

// Visually hidden text that names the icon-only button.
const HiddenLabel = styled.span`
	position: absolute;
	width: 1px;
	height: 1px;
	overflow: hidden;
	clip: rect(0 0 0 0);
	white-space: nowrap;
`;

let labelCount = 0;

type LabelledIconButtonProps = {
	icon: IconName;
	label: string;
	onPress: () => void;
	disabled?: boolean;
	emphasis?: Emphasis;
	'data-cy'?: string;
};

// An icon-only design-system Button with an accessible name. The Button overwrites aria-label with its
// icon name, so the name comes from a hidden label that aria-labelledby points at.
export const LabelledIconButton = ({
	icon,
	label,
	onPress,
	disabled,
	emphasis = EMPHASIS.LOW,
	...rest
}: LabelledIconButtonProps): JSX.Element => {
	// A lazy initialiser runs once per mounted button, so the id is fixed for the life of the button.
	const [labelId] = useState(() => `labelled-icon-button-${++labelCount}`);
	return (
		<>
			<HiddenLabel id={labelId}>{label}</HiddenLabel>
			<Button
				emphasis={emphasis}
				icon={icon}
				iconOnly
				aria-labelledby={labelId}
				disabled={disabled}
				onPress={onPress}
				data-cy={rest['data-cy']}
			/>
		</>
	);
};
